/**
 * Quién tiene acceso — gestión (F3b). Solo el owner otorga y revoca (regla de
 * Passbolt: ni un admin puede darse acceso). Revocar marca la credencial como
 * "rotación sugerida" y avisa al owner.
 */
import { useMemo, useState } from 'react';
import { Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GrantsList, principalLabel, useVaultLabels } from './vault-ui';
import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { errMsg } from '@/hooks/useVault';
import {
  VAULT_AREAS,
  areaGroupPrincipal,
  parsePrincipal,
  userPrincipal,
  type VaultGrant,
  type VaultPermission,
} from '../../../../shared/types/vault';

interface GrantsPanelProps {
  secretId: string;
  grants: VaultGrant[];
  canOwn: boolean;
}

export function GrantsPanel({ secretId, grants, canOwn }: GrantsPanelProps) {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const utils = trpc.useUtils();
  const invalidate = () => {
    utils.vault.getSecret.invalidate({ secretId });
    utils.vault.listSecrets.invalidate();
  };
  const grant = trpc.vault.grantAccess.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(t('vault.grants.added', 'Acceso otorgado'));
      setEmail('');
      setPicked(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const revoke = trpc.vault.revokeAccess.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success(t('vault.grants.revoked', 'Acceso revocado · la credencial quedó marcada para rotación'));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const [kind, setKind] = useState<'user' | 'area'>('user');
  const [email, setEmail] = useState('');
  const [picked, setPicked] = useState<{ name: string; email: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // personas activas del community map (cache 5 min); solo se pide cuando el owner ve el panel
  const members = trpc.vault.listMembersForPicker.useQuery(undefined, { staleTime: 5 * 60_000, enabled: canOwn });
  const alreadyGranted = useMemo(
    () => new Set(grants.map((g) => parsePrincipal(g.principal)).filter((p): p is { kind: 'user'; email: string } => !!p && p.kind === 'user').map((p) => p.email.toLowerCase())),
    [grants]
  );
  const [area, setArea] = useState<string>(VAULT_AREAS[0].id);
  const [permission, setPermission] = useState<VaultPermission>('read');
  const [confirmRevoke, setConfirmRevoke] = useState<VaultGrant | null>(null);

  const principal = kind === 'user' ? userPrincipal(email.trim()) : areaGroupPrincipal(area);
  const canAdd = kind === 'user' ? /^[^@\s]+@winclap\.com$/i.test(email.trim()) : true;
  const permissionOptions: VaultPermission[] = kind === 'user' ? ['read', 'edit', 'owner'] : ['read', 'edit'];

  if (!canOwn) return <GrantsList grants={grants} />;

  return (
    <div className="space-y-3">
      <ul className="divide-y">
        {grants.map((g) => {
          const p = principalLabel(g.principal, t);
          return (
            <li key={g.principal} className="flex items-center gap-2 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.label}</div>
                <div className="truncate text-xs text-muted-foreground">{p.sub || L.perm(g.permission)}</div>
              </div>
              <span className="text-xs text-muted-foreground">{L.perm(g.permission)}</span>
              <Button variant="ghost" size="icon" title={t('vault.grants.revoke', 'Revocar')} aria-label={t('vault.grants.revoke', 'Revocar')} onClick={() => setConfirmRevoke(g)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          );
        })}
      </ul>

      <div className="rounded-md border p-3 space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.grants.add_title', 'Dar acceso')}</div>
        {/* Apilado, no en grilla: la Card mide 360 px y el mail quedaba en 40 px (Marcos, 27/09). */}
        <div className="grid grid-cols-2 gap-2">
          <Select value={kind} onValueChange={(v) => { setKind(v as 'user' | 'area'); setPicked(null); setEmail(''); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="user">{t('vault.grants.kind_user', 'Persona')}</SelectItem>
              <SelectItem value="area">{t('vault.grants.kind_area', 'Área')}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={permission} onValueChange={(v) => setPermission(v as VaultPermission)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {permissionOptions.map((p) => (
                <SelectItem key={p} value={p}>{L.perm(p)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {kind === 'user' ? (
          picked ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{picked.name}</div>
                <div className="truncate text-xs text-muted-foreground">{picked.email}</div>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => { setPicked(null); setEmail(''); }} title={t('vault.grants.pick_clear', 'Cambiar persona')} aria-label={t('vault.grants.pick_clear', 'Cambiar persona')}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button type="button" variant="outline" className="w-full justify-start font-normal text-muted-foreground" onClick={() => setPickerOpen(true)} data-tour="grant-pick">
              <Search className="mr-2 h-4 w-4" />
              {t('vault.grants.pick', 'Buscar en el community map')}
            </Button>
          )
        ) : (
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {VAULT_AREAS.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button size="sm" className="w-full" disabled={!canAdd || grant.isPending} onClick={() => grant.mutate({ secretId, principal, permission })} data-tour="grant-add">
          <Plus className="mr-1 h-4 w-4" />
          {t('vault.grants.add', 'Agregar')}
        </Button>
        <p className="text-xs text-muted-foreground">{t('vault.grants.hint', 'Owner siempre es una persona y puede haber varios; al último no se lo puede quitar. Un grupo recibe como máximo "Editar".')}</p>
      </div>

      <MemberPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        title={t('vault.grants.pick_title', 'Dar acceso a una persona')}
        description={t('vault.grants.pick_desc', 'Escribí el nombre o el mail: se sugieren las personas activas del community map.')}
        searchPlaceholder={t('vault.grants.pick_search', 'Nombre o mail…')}
        noResultsText={t('vault.grants.pick_none', 'Sin resultados')}
        members={members.data ?? []}
        loading={members.isLoading}
        exclude={alreadyGranted}
        onPick={(m) => {
          setPicked({ name: m.name, email: m.email });
          setEmail(m.email);
          setPickerOpen(false);
        }}
      />

      <AlertDialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.grants.revoke_title', '¿Revocar el acceso?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('vault.grants.revoke_body', 'Quien tuvo acceso pudo copiar la contraseña: la credencial va a quedar con rotación sugerida y se le avisa al owner.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={revoke.isPending}
              onClick={() => {
                if (confirmRevoke) revoke.mutate({ secretId, principal: confirmRevoke.principal });
                setConfirmRevoke(null);
              }}
            >
              {t('vault.grants.revoke', 'Revocar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
